// src/packages/care/prediction/predictionToCalendarItems.ts
//
// Bridge from the FertileWindowPrediction contract to the calendar's CalendarItem model, so the
// calendar presentation renders the model's
// output with no UI changes. This is the only place the prediction contract meets the render model.
//
// Classification: a fertile/ovulation day in the FUTURE (date > today) is a prediction (rendered
// faded via meta.predicted); today-or-past days are the current-cycle estimate (rendered solid).
// The next period is always a future prediction.

import {
  createFertileItem,
  createPeriodItem,
  generatePeriodDays,
  type CalendarItem,
} from "../calendar.types";
import { addDays, daysBetween } from "./cycleStats";
import type { FertileWindowPrediction, LocalDate } from "./fertileWindow.types";

function withPredictionMetadata(
  item: CalendarItem,
  prediction: FertileWindowPrediction,
): CalendarItem {
  return {
    ...item,
    meta: {
      ...item.meta,
      predictionConfidence: prediction.confidence,
      predictionStatus: prediction.status,
      predictionWarnings: prediction.warnings,
    },
  };
}

export function predictionToCalendarItems(
  prediction: FertileWindowPrediction,
  today: LocalDate,
): CalendarItem[] {
  const items: CalendarItem[] = [];
  const ovulationDate = prediction.ovulation?.mostLikely ?? null;

  for (const fertileDay of prediction.fertileDays) {
    const isOvulation = fertileDay.date === ovulationDate;
    const item = withPredictionMetadata(
      createFertileItem(fertileDay.date, isOvulation),
      prediction,
    );
    if (fertileDay.date > today) {
      item.meta = { ...item.meta, predicted: true };
    }
    items.push(item);
  }

  // Predicted next-period days. nextPeriod is the ONSET uncertainty band, so its midpoint is the
  // most-likely onset; the bleed span uses the calendar's default period length.
  if (prediction.nextPeriod) {
    const { start, end } = prediction.nextPeriod;
    const onset = addDays(start, Math.round(daysBetween(start, end) / 2));
    if (onset > today) {
      for (const day of generatePeriodDays(onset, null)) {
        const item = withPredictionMetadata(createPeriodItem(day), prediction);
        item.meta = { ...item.meta, predicted: true, source: "computed" };
        items.push(item);
      }
    }
  }

  return items;
}
